import BaseComponent from '@utils/base.js';
import tag from '@utils/utilTags.js';
import Change from '@components/change/change.js';
import ControlBtn from '@components/controlBtn/controlBtn.js';

import * as styles from './modal.module.scss';

/** @extends {BaseComponent<HTMLDialogElement>} */
export default class Modal extends BaseComponent {
  /** @type {BaseComponent<HTMLDivElement>} */
  change;
  /** @type {BaseComponent<HTMLDivElement>} */
  content;
  constructor() {
    super({ elementTag: 'dialog', classes: [styles.modal] });

    this.change = new Change({ closeModal: this.closeModal });

    this.createModal();
    this.setListeners();
  }

  /**
   * Sets event listeners.
   * @returns {void}
   */
  setListeners() {
    window.addEventListener('custom:showChange', this.showChange);

    window.addEventListener(
      'custom:showWinMessage',
      /** @type {EventListener} */ (this.showWinMessage)
    );

    window.addEventListener('custom:showNoSaveMessage', () => {
      this.showModal(
        tag.div({
          text: 'No save yet. Try to make it.',
          classes: [styles.message],
        })
      );
    });

    window.addEventListener(
      'custom:showResults',
      /** @type {EventListener} */ (this.showResults)
    );

    window.addEventListener('custom:showDisallowSave', () => {
      this.showModal(
        tag.div({
          text: 'Cannot save while reviewing the solution.',
          classes: [styles.message],
        })
      );
    });

    this.addListener('click', (e) => {
      if (e.target === e.currentTarget) this.element.close();
    });
  }

  /**
   * Creates modal.
   * @returns {void}
   */
  createModal = () => {
    const wrapper = tag.div({ classes: [styles.modalWrapper] });
    this.appendSingle(wrapper);

    const closeBtn = new ControlBtn({ text: 'close' });
    this.content = tag.div({ classes: [styles.content] });
    wrapper.appendChildren(this.content, closeBtn);
    closeBtn.addListener('click', this.closeModal);
  };

  /**
   * Closes modal.
   * @returns {void}
   */
  closeModal = () => {
    this.element.close();
  };

  /**
   * Displays modal with specified component.
   * @param {BaseComponent<HTMLElement>} component
   * @returns {any}
   */
  showModal(component) {
    this.content.getElement().childNodes.forEach((item) => item.remove());
    this.content.appendSingle(component);

    this.element.showModal();
  }

  /**
   * Shows modal with difficulty options.
   * @returns {void}
   */
  showChange = () => {
    this.showModal(this.change);
  };

  /**
   * Creates a win message with a specified time.
   * @param {number} time
   * @returns {BaseComponent<HTMLDivElement>}
   */
  createWinMessage = (time) =>
    tag.div({
      text: `Great! You have solved the nonogram in ${time} seconds!`,
      classes: [styles.message],
    });

  /**
   * Shows modal with win message.
   * @param {CustomEvent<{time: number}>} e
   * @returns {void}
   */
  showWinMessage = (e) => {
    const { time } = e.detail;
    this.showModal(this.createWinMessage(time));
  };

  /**
   * Creates a message with specified game results.
   * @param {Result[]} results
   * @returns {BaseComponent<HTMLDivElement>}
   */
  createResults = (results) => {
    const table = tag.div(
      { classes: [styles.table] },
      tag.div({ text: '№', classes: [styles.head] }),
      tag.div({ text: 'Name', classes: [styles.head] }),
      tag.div({ text: 'Difficulty', classes: [styles.head] }),
      tag.div({ text: 'Time', classes: [styles.head] })
    );

    results.forEach((result, i) => {
      table.appendChildren(
        tag.div({ text: (i + 1).toString(), classes: [styles.cell] }),
        tag.div({ text: result.name, classes: [styles.cell] }),
        tag.div({ text: result.difficulty, classes: [styles.cell] }),
        tag.div({ text: result.formattedTime, classes: [styles.cell] })
      );
    });

    return table;
  };

  /**
   * Shows modal with win message.
   * @param {CustomEvent<{results: Result[]}>} e
   * @returns {void}
   */
  showResults = (e) => {
    const { results } = e.detail;
    if (!results || results.length === 0) {
      this.showModal(
        tag.div({
          text: 'No results yet. Try to solve a nonogram.',
          classes: [styles.message],
        })
      );
    } else {
      this.showModal(this.createResults(results));
    }
  };
}
